import React from 'react';
import VMasker from 'vanilla-masker'; // https://github.com/vanilla-masker/vanilla-masker
import moment from 'moment';
import RichTextEditor, { createValueFromString } from 'react-rte';
import { chefVIPIndicator, ACCOUNT_STATUSES, SERVICE_TYPES } from './const.js';
import { get as _get } from 'lodash';

export function maskPhone(val) {
  return VMasker.toPattern(val, '999-999-9999');
}

export function maskCreditCard(val) {
  return VMasker.toPattern(val, '9999 9999 9999 9999');
}

export function maskExpDate(val) {
  return VMasker.toPattern(val, '99 / 99');
}

export function maskCVC(val) {
  return VMasker.toPattern(val, '999');
}

export function maskMoney(val) {
  return VMasker.toMoney(val || 0, {
    separator: '.',
    delimiter: ',',
    unit: '$',
  });
}
export function maskDate(val) {
  return moment(val).format('MM/DD/YY');
}
export function maskDateTime(val) {
  return moment(val).format('MM/DD/YY [at] h:mma');
}
export function maskDateSQL(val) {
  return moment(val).format('YYYY-MM-DD');
}
export function maskServiceWeek(val) {
  if (!val) return null;
  const start = moment(val).format('MM/DD/YY');
  const end = moment(val).add(6, 'days').format('MM/DD/YY');
  return `${start} - ${end}`;
}

export function maskServiceDate(val) {
  if (!val) return null;
  return moment(val).format('dddd[,] MMM D[,] Y \n\r');
}

export function maskServiceDateNoDay(val) {
  if (!val) return null;
  return moment(val).format('MMM Do[,] Y \n\r');
}

export function maskServiceDateShort(val) {
  return moment(val).format('ddd[,] MM/DD/YY');
}

export function maskServiceDateNoYear(val) {
  if (!val) return null;
  return moment(val).format('dddd[,] MMM D');
}

export function maskServiceStartTime(val) {
  if (!val) return null;
  return moment(val).format('h:mma');
}
export function maskServiceEndTime(val) {
  if (!val) return null;
  return moment(val).format(' - h:mma');
}

export function maskDateOff(val, compare) {
  if (!val || (compare && moment(compare).isSame(moment(val), 'day')))
    return null;
  return moment(val).format('ddd[,] MMM D, YYYY');
}

export function maskTime(val) {
  if (!val) return null;
  return moment(val).format('h:mma');
}

export function maskWeeklyServiceBlock(val) {
  if (!val) return null;
  const start = moment(val, moment.HTML5_FMT.TIME).format('h:mma');
  const end = moment(val, moment.HTML5_FMT.TIME)
    .add(4, 'hour')
    .format(' - h:mma');
  return start + end;
}

export function maskWeeklyServiceBlockAbbrv(val) {
  if (!val) return null;
  const start = moment(val, moment.HTML5_FMT.TIME).format('ha');
  const end = moment(val, moment.HTML5_FMT.TIME).add(4, 'hour').format(' - ha');
  return start + end;
}

export function maskCapitalizeFirstLetter(val) {
  return val ? val.charAt(0).toUpperCase() + val.slice(1) : '';
}

export function maskCapitalizeFirstLetterOnly(val) {
  return val ? val.charAt(0).toUpperCase() + val.slice(1).toLowerCase() : '';
}

export function maskLiveDate(val) {
  if (!val) return null;
  return moment(val).subtract(4, 'week').format('MM/DD/YY');
}

export function maskWeek(val) {
  if (!val) return null;
  return `${moment(val).format('MM/DD/YYYY')} - ${moment(val)
    .add(6, 'days')
    .format('MM/DD/YYYY')}`;
}

export function maskWeekOf(val) {
  if (!val) return null;
  return moment(val).format('[Week of] MMM Do');
}

export function maskDayOfWeekUnlimited(val) {
  if (!val) return null;
  return moment(val).calendar(null, {
    sameDay: '[Today]',
    nextDay: '[Tomorrow]',
    nextWeek: 'dddd',
    lastDay: '[Yesterday]',
    lastWeek: '[Last] dddd',
    sameElse: 'MM/DD/YYYY',
  });
}

export function maskRichText(val) {
  if (!val) return '';
  const value = createValueFromString(val, 'html');
  return (
    <RichTextEditor
      value={value}
      readOnly={true}
      className="cms__inline-rich-text"
    />
  );
}

export const maskAccountStatus = (status) =>
  ({
    verification_pending: 'Pending',
    [ACCOUNT_STATUSES.ACTIVE]: 'Active',
    [ACCOUNT_STATUSES.DEACTIVATED]: 'Deactivated',
    [ACCOUNT_STATUSES.INACTIVE]: 'Inactive',
  }[status] || 'N/A');

export const maskServiceDistricts = (districts) => {
  if (typeof districts === 'string' || districts instanceof String)
    return districts;
  else return districts ? districts.map((d) => d.name).join(',') : [];
};

// not a better place for this
export const portionSwitch = (portion) =>
  ({
    small: 1,
    medium: 2,
    large: 3,
  }[portion] || 1);

export const reversePortionSwitch = (portion) =>
  ({
    '1.0': 'small',
    '2.0': 'medium',
    '3.0': 'large',
  }[portion] || '');

export const maskPrependComma = (value) => {
  return value ? `, ${value}` : ' ';
};

export const maskSlug = (text) => text.toLowerCase().replace(/[\W_]+/g, '-');

export const maskTimeBlock = (block) => {
  const [hour = 0, minute = 0] = block.split(':');
  return moment().hour(hour).minute(minute).format('h:mma');
};

export const maskDayOfWeek = (dayOfWeek) =>
  ({
    0: 'Sun',
    1: 'Mon',
    2: 'Tue',
    3: 'Wed',
    4: 'Thu',
    5: 'Fri',
    6: 'Sat',
  }[dayOfWeek] || String.fromCharCode(8212));

export const serviceTypeSwitch = (type) =>
  ({
    [SERVICE_TYPES.WEEKLY]: 'Weekly',
    [SERVICE_TYPES.CUSTOM_WEEKLY]: 'Custom Weekly',
    [SERVICE_TYPES.OCCASION]: 'Occasion',
    [SERVICE_TYPES.MARKETING_OCCASION]: 'Marketing Occasion',
    [SERVICE_TYPES.CUSTOM_OCCASION]: 'Custom Occasion',
    [SERVICE_TYPES.COOKING_CLASS]: 'Cooking Class',
  }[type] || 'N/A');

export const portionSizeSwitch = (type) =>
  ({
    small: 'Small',
    medium: 'Medium',
    large: 'Large',
  }[type] || 'N/A');

export const portionAbbreviationSwitch = (type) =>
  ({
    '1.0': 'S',
    '2.0': 'M',
    '3.0': 'L',
  }[type] || '');

export const addPortionSizeToNotes = (portionName, note) =>
  `${portionName} Portion; ` + note;

export const removePortionSizeFromNotes = (note) =>
  typeof note === 'string'
    ? note.replace(/^(Small|Medium|Large) Portion; /, '')
    : '';

export const maskIsVip = (val) => (val ? chefVIPIndicator.repeat(val) : '');

export const adminChefName = (chef) =>
  _get(chef, 'first_name', '') +
  ' ' +
  _get(chef, 'last_name', '') +
  maskIsVip(_get(chef, 'vip'));

export const adminClientName = (client, brightGreen = false) => {
  let name = _get(client, 'first_name', '') + ' ' + _get(client, 'last_name', '') 
  switch(_get(client, 'status')) {
    case 'vip':
      return (<div className={'status--vip' + (brightGreen ? '-bright' : '') }>{name}</div>)
    case 'high_alert':
      return (<div className='status--high-alert'>{name}</div>)
    case 'probation':
      return (<div className='status--probation'>{name}</div>)
    default:
      return name
  }
}




// WEBPACK FOOTER //
// ./src/masks.js